import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { getCollection } from '@/services/data-sources/get-collection';
import { Collection } from '@/types/source-types';

export const collectionQueryKey = (orgId?: string, collectionId?: string) =>
  ['collection', orgId, collectionId] as const;

interface UseGetCollectionOptions
  extends Omit<UseQueryOptions<Collection>, 'queryKey' | 'queryFn'> {
  collectionId?: string;
  orgId?: string;
}

export const useGetCollection = (options?: UseGetCollectionOptions) => {
  const { collectionId, orgId, ...queryOptions } = options || {};

  return useQuery<Collection>({
    queryKey: collectionQueryKey(orgId, collectionId),
    queryFn: async () => {
      if (!collectionId) {
        throw new Error('collectionId is required');
      }
      const response = await getCollection(collectionId);
      if (
        response &&
        'detail' in response &&
        typeof response.detail === 'string'
      ) {
        throw new Error(response.detail);
      }
      if (!response) {
        throw new Error('Collection not found');
      }
      return response as Collection;
    },
    staleTime: 30 * 1000,
    // getCollection returns { detail } deterministically for not-found /
    // access-denied, so retrying can't help — fail fast to keep navigation
    // and error toasts snappy (matches the old single-fetch behavior).
    retry: false,
    ...queryOptions,
    enabled: !!collectionId && !!orgId && (queryOptions.enabled ?? true),
  });
};
